import type { Attachment } from '../types';

const OFFLINE_QUEUE_STORAGE_KEY = 'ally_offline_queue_v1';

function normalizeOwnerKey(ownerKey: string | null | undefined) {
  const normalized = String(ownerKey || '').trim();
  return normalized || null;
}

function getOfflineQueueStorageKey(ownerKey: string | null | undefined) {
  const normalizedOwnerKey = normalizeOwnerKey(ownerKey);
  return normalizedOwnerKey ? `${OFFLINE_QUEUE_STORAGE_KEY}:${encodeURIComponent(normalizedOwnerKey)}` : null;
}

export interface OfflineQueueFileAttachment {
  kind: 'file';
  name: string;
  type: string;
  size: number;
  lastModified: number;
  dataUrl: string;
}

export interface OfflineQueueStoredAttachment {
  kind: 'attachment';
  attachment: Attachment;
}

export type OfflineQueueAttachment = OfflineQueueFileAttachment | OfflineQueueStoredAttachment;

export interface OfflineQueueItem {
  id: string;
  messageId: string;
  conversationId: string;
  content: string;
  createdAt: string;
  attachments: OfflineQueueAttachment[];
  user?: {
    name: string;
    email?: string;
  };
}

function getStorage() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  const storage = window.localStorage;
  if (
    typeof storage.getItem !== 'function'
    || typeof storage.setItem !== 'function'
    || typeof storage.removeItem !== 'function'
  ) {
    return null;
  }

  return storage;
}

function normalizeAttachment(input: any): Attachment {
  return {
    id: String(input?.id ?? crypto.randomUUID()),
    messageId: input?.messageId ?? input?.message_id,
    filename: input?.filename ?? 'attachment',
    contentType: input?.contentType ?? input?.content_type ?? input?.mime_type ?? 'application/octet-stream',
    url: input?.url ?? '',
    size: Number(input?.size ?? 0),
    createdAt: input?.createdAt ?? input?.created_at ?? new Date().toISOString(),
    dataUrl: typeof input?.dataUrl === 'string' ? input.dataUrl : undefined,
  };
}

function normalizeQueueItem(input: any): OfflineQueueItem | null {
  const id = String(input?.id || '').trim();
  const messageId = String(input?.messageId || '').trim();
  const conversationId = String(input?.conversationId || '').trim();

  if (!id || !messageId || !conversationId) {
    return null;
  }

  const attachments = Array.isArray(input?.attachments)
    ? input.attachments.flatMap((attachment: any) => {
        if (attachment?.kind === 'file' && typeof attachment?.dataUrl === 'string') {
          return [{
            kind: 'file' as const,
            name: String(attachment?.name || 'attachment'),
            type: String(attachment?.type || 'application/octet-stream'),
            size: Number(attachment?.size || 0),
            lastModified: Number(attachment?.lastModified || 0),
            dataUrl: attachment.dataUrl,
          }];
        }

        if (attachment?.kind === 'attachment' && attachment?.attachment) {
          return [{
            kind: 'attachment' as const,
            attachment: normalizeAttachment(attachment.attachment),
          }];
        }

        return [];
      })
    : [];

  return {
    id,
    messageId,
    conversationId,
    content: String(input?.content ?? ''),
    createdAt: input?.createdAt ?? new Date().toISOString(),
    attachments,
    user: input?.user && typeof input.user === 'object' && typeof input.user.name === 'string'
      ? {
          name: input.user.name,
          email: typeof input.user.email === 'string' ? input.user.email : undefined,
        }
      : undefined,
  };
}

export function loadOfflineQueue(ownerKey: string | null): OfflineQueueItem[] {
  const storage = getStorage();
  const storageKey = getOfflineQueueStorageKey(ownerKey);
  if (!storage || !storageKey) {
    return [];
  }

  try {
    const raw = JSON.parse(storage.getItem(storageKey) || '[]');
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map((item) => normalizeQueueItem(item))
      .filter((item): item is OfflineQueueItem => Boolean(item));
  } catch {
    return [];
  }
}

export function saveOfflineQueue(items: OfflineQueueItem[], ownerKey: string | null) {
  const storage = getStorage();
  const storageKey = getOfflineQueueStorageKey(ownerKey);
  if (!storage || !storageKey) {
    return;
  }

  storage.setItem(storageKey, JSON.stringify(items));
}

export async function serializeOfflineAttachments(attachments: Array<File | Attachment>): Promise<OfflineQueueAttachment[]> {
  const serialized = await Promise.all(attachments.map(async (attachment) => {
    if (attachment instanceof File) {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(reader.error || new Error('Failed to read file for offline queue'));
        reader.readAsDataURL(attachment);
      });

      return {
        kind: 'file' as const,
        name: attachment.name,
        type: attachment.type || 'application/octet-stream',
        size: attachment.size,
        lastModified: attachment.lastModified,
        dataUrl,
      };
    }

    return {
      kind: 'attachment' as const,
      attachment: normalizeAttachment(attachment),
    };
  }));

  return serialized.filter(Boolean);
}

function dataUrlToFile(dataUrl: string, name: string, type: string, lastModified: number) {
  const parts = dataUrl.split(',');
  const base64 = parts[1] || '';
  const mime = /data:([^;]+);base64/i.exec(parts[0] || '')?.[1] || type || 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], name, { type: mime, lastModified: Number(lastModified || Date.now()) });
}

export function deserializeOfflineAttachments(attachments: OfflineQueueAttachment[]): Array<File | Attachment> {
  return attachments.reduce<Array<File | Attachment>>((deserialized, attachment) => {
    if (attachment.kind === 'file') {
      deserialized.push(dataUrlToFile(attachment.dataUrl, attachment.name, attachment.type, attachment.lastModified));
      return deserialized;
    }

    if (attachment.kind === 'attachment') {
      deserialized.push(normalizeAttachment(attachment.attachment));
    }

    return deserialized;
  }, []);
}

export function upsertOfflineQueueItem(item: OfflineQueueItem, ownerKey: string | null) {
  const items = loadOfflineQueue(ownerKey);
  const nextItems = [item, ...items.filter((entry) => entry.id !== item.id)];
  saveOfflineQueue(nextItems, ownerKey);
  return nextItems;
}

export function removeOfflineQueueItem(itemId: string, ownerKey: string | null) {
  const items = loadOfflineQueue(ownerKey).filter((entry) => entry.id !== itemId);
  saveOfflineQueue(items, ownerKey);
  return items;
}
